import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { Card, Input, Select, Spinner, LoadingState } from '../../components/ui';
import { ACTION_COLORS } from '../../constants/actionColors';

const FILTERABLE_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'SUBMIT', 'LOCK', 'UPLOAD', 'ASSIGN', 'WARN', 'CLONE', 'RESOLVE', 'FORCE_SUBMIT'];
const ENTITIES = ['User', 'Exam', 'Question', 'ExamAttempt', 'Violation', 'Message', 'Auth', 'Settings'];
const ROLES = ['SUPER_ADMIN', 'ADMIN', 'PROCTOR', 'STUDENT'];

const ActivityTimeline = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loaderRef = useRef(null);
  const LIMIT = 50;

  const buildQuery = () => {
    const params = new URLSearchParams({ limit: LIMIT, page: 1 });
    if (search) params.set('search', search);
    if (filterAction) params.set('action', filterAction);
    if (filterEntity) params.set('entity', filterEntity);
    if (filterRole) params.set('role', filterRole);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    return params;
  };

  const fetchLogs = useCallback(async (reset = false) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const params = buildQuery();
      if (!reset) params.set('page', Math.floor(offset / LIMIT) + 2);

      const res = await api.get(`/super-admin/logs?${params.toString()}`);
      const newLogs = res.data.data || [];
      const pagination = res.data.pagination;

      if (reset) {
        setLogs(newLogs);
        setOffset(newLogs.length);
      } else {
        setLogs(prev => [...prev, ...newLogs]);
        setOffset(prev => prev + newLogs.length);
      }

      setHasMore(pagination.page < pagination.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, filterAction, filterEntity, filterRole, dateFrom, dateTo, offset]);

  // Refetch on filter change
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    const params = new URLSearchParams({ limit: LIMIT, page: 1 });
    if (search) params.set('search', search);
    if (filterAction) params.set('action', filterAction);
    if (filterEntity) params.set('entity', filterEntity);
    if (filterRole) params.set('role', filterRole);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);

    setLoading(true);
    api.get(`/super-admin/logs?${params.toString()}`)
      .then(res => {
        const newLogs = res.data.data || [];
        setLogs(newLogs);
        setOffset(newLogs.length);
        setHasMore(res.data.pagination.page < res.data.pagination.pages);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, filterAction, filterEntity, filterRole, dateFrom, dateTo]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const params = new URLSearchParams({ limit: LIMIT, page: Math.floor(logs.length / LIMIT) + 1 });
          if (search) params.set('search', search);
          if (filterAction) params.set('action', filterAction);
          if (filterEntity) params.set('entity', filterEntity);
          if (filterRole) params.set('role', filterRole);
          if (dateFrom) params.set('dateFrom', dateFrom);
          if (dateTo) params.set('dateTo', dateTo);

          setLoadingMore(true);
          api.get(`/super-admin/logs?${params.toString()}`)
            .then(res => {
              const newLogs = res.data.data || [];
              setLogs(prev => {
                const ids = new Set(prev.map(l => l._id));
                const unique = newLogs.filter(l => !ids.has(l._id));
                return [...prev, ...unique];
              });
              setHasMore(res.data.pagination.page < res.data.pagination.pages);
            })
            .catch(console.error)
            .finally(() => setLoadingMore(false));
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, logs.length, search, filterAction, filterEntity, filterRole, dateFrom, dateTo]);

  // Auto-refresh every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      const params = new URLSearchParams({ limit: 10, page: 1 });
      api.get(`/super-admin/logs?${params.toString()}`)
        .then(res => {
          const newest = res.data.data || [];
          setLogs(prev => {
            const ids = new Set(prev.map(l => l._id));
            const fresh = newest.filter(l => !ids.has(l._id));
            return fresh.length ? [...fresh, ...prev] : prev;
          });
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const c = (action) => ACTION_COLORS.dark[action] || ACTION_COLORS.dark.OTHER;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Activity Timeline</h2>
          <p className="text-slate-500 text-sm mt-1">Every action performed on the platform — real-time</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-green-400 text-xs">Live</span>
        </div>
      </div>

      {/* Filters */}
      <Card tone="dark" padding="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Input tone="dark" className="col-span-2" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
          <Select tone="dark" value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="">All Actions</option>
            {FILTERABLE_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
          <Select tone="dark" value={filterEntity} onChange={e => setFilterEntity(e.target.value)}>
            <option value="">All Entities</option>
            {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
          </Select>
          <Select tone="dark" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
          <div className="flex gap-1">
            <Input tone="dark" type="date" className="text-xs" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <Input tone="dark" type="date" className="text-xs" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Timeline */}
      <Card tone="dark">
        {loading ? (
          <LoadingState tone="dark" label="Loading activity…" className="py-12" />
        ) : logs.length === 0 ? (
          <p className="text-slate-600 text-center py-12">No activity logs found.</p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[15px] top-0 bottom-0 w-px bg-slate-800" />

            <div className="space-y-1">
              {logs.map((log) => {
                const clr = c(log.action);
                return (
                  <div
                    key={log._id}
                    className="relative flex items-start gap-4 pl-10 py-3 rounded-lg hover:bg-slate-800/40 cursor-pointer transition-colors group"
                    onClick={() => setSelectedLog(selectedLog?._id === log._id ? null : log)}
                  >
                    {/* Dot */}
                    <div className={`absolute left-[9px] w-3 h-3 rounded-full ${clr.dot} border-2 border-slate-950 flex-shrink-0 mt-1.5`} />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${clr.bg} ${clr.text} ${clr.border}`}>
                          {log.action}
                        </span>
                        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
                          {log.entity}
                        </span>
                        <span className="text-slate-300 text-sm">{log.description}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-slate-600 text-xs">{new Date(log.createdAt).toLocaleString()}</span>
                        {log.ipAddress && (
                          <span className="text-slate-700 text-xs">{log.ipAddress}</span>
                        )}
                        {log.userRole && (
                          <span className="text-slate-600 text-xs border border-slate-800 px-1.5 py-0.5 rounded">
                            {log.userRole}
                          </span>
                        )}
                      </div>

                      {/* Expanded detail */}
                      {selectedLog?._id === log._id && (
                        <div className="mt-3 bg-slate-800/80 rounded-lg p-4 border border-slate-700/50">
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div><span className="text-slate-500">User:</span> <span className="text-white">{log.userName}</span></div>
                            <div><span className="text-slate-500">Role:</span> <span className="text-white">{log.userRole}</span></div>
                            <div><span className="text-slate-500">Email:</span> <span className="text-white">{log.userEmail || '—'}</span></div>
                            <div><span className="text-slate-500">IP:</span> <span className="text-white">{log.ipAddress || '—'}</span></div>
                            <div><span className="text-slate-500">Route:</span> <span className="text-white font-mono">{log.method} {log.route}</span></div>
                            <div><span className="text-slate-500">Status:</span> <span className="text-white">{log.statusCode}</span></div>
                            {log.entityId && <div><span className="text-slate-500">Entity ID:</span> <span className="text-white font-mono text-xs">{String(log.entityId)}</span></div>}
                            {log.entityName && <div><span className="text-slate-500">Entity Name:</span> <span className="text-white">{log.entityName}</span></div>}
                          </div>
                          {log.dataSnapshot && (
                            <div className="mt-3">
                              <p className="text-slate-500 text-xs mb-1">Deleted Data Snapshot:</p>
                              <pre className="text-xs text-emerald-400 bg-slate-950 rounded p-3 overflow-auto max-h-48 font-mono">
                                {JSON.stringify(log.dataSnapshot, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.changes && (
                            <div className="mt-3">
                              <p className="text-slate-500 text-xs mb-1">Changes:</p>
                              <pre className="text-xs text-blue-400 bg-slate-950 rounded p-3 overflow-auto max-h-48 font-mono">
                                {JSON.stringify(log.changes, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.metadata && (
                            <div className="mt-3">
                              <p className="text-slate-500 text-xs mb-1">Metadata:</p>
                              <pre className="text-xs text-slate-400 bg-slate-950 rounded p-3 overflow-auto max-h-32 font-mono">
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite scroll loader */}
            <div ref={loaderRef} className="py-4 flex justify-center">
              {loadingMore && <Spinner size="sm" className="text-violet-500" />}
              {!hasMore && logs.length > 0 && (
                <p className="text-slate-700 text-xs">All logs loaded ({logs.length} total)</p>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ActivityTimeline;
